import React, { useMemo, useState } from 'react';

function cleanName(value) {
  return typeof value === 'string' ? value.trim() : '';
}

export default function RolesEditor({
  roles = [],
  presetRoles = [],
  teamTags = [],
  memberOptions = [],
  onAddPreset,
  onQuickAdd,
  onUpdateRole,
  onResizeSlots,
  onToggleAllowedTag,
  onAssignSlot,
  onClearSlot,
  onRemoveRole,
  coverageScope = 'event',
  coverageHint = '',
}) {
  const [presetId, setPresetId] = useState('');
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleSlots, setNewRoleSlots] = useState(1);
  const [newRoleAllowedTags, setNewRoleAllowedTags] = useState([]);
  const [newRoleRequiresApproval, setNewRoleRequiresApproval] = useState(true);
  const [saveAsPreset, setSaveAsPreset] = useState(false);
  const [assignBySlot, setAssignBySlot] = useState({});
  const [searchBySlot, setSearchBySlot] = useState({});
  const [quickAddOpen, setQuickAddOpen] = useState(false);

  const tagOptions = useMemo(
    () => (Array.isArray(teamTags) ? teamTags.filter((t) => t?.name).map((t) => t.name) : []),
    [teamTags]
  );

  const toggleQuickTag = (tagName) => {
    setNewRoleAllowedTags((prev) => {
      const next = new Set(prev);
      if (next.has(tagName)) next.delete(tagName);
      else next.add(tagName);
      return Array.from(next);
    });
  };

  const handleAddPreset = () => {
    if (!presetId) return;
    onAddPreset?.(presetId);
    setPresetId('');
  };

  const handleQuickAdd = async () => {
    const name = cleanName(newRoleName);
    if (!name) return;

    await onQuickAdd?.({
      name,
      slots: Math.max(1, Number(newRoleSlots) || 1),
      allowedTags: newRoleAllowedTags,
      requiresApproval: newRoleRequiresApproval,
      saveAsPreset,
    });

    setNewRoleName('');
    setNewRoleSlots(1);
    setNewRoleAllowedTags([]);
    setNewRoleRequiresApproval(true);
    setSaveAsPreset(false);
    setQuickAddOpen(false);
  };

  const normalizeSearch = (value) =>
    typeof value === 'string' ? value.trim().toLowerCase() : '';

  const getMemberSearchText = (member) =>
    [
      member?.name,
      member?.firstName,
      member?.lastName,
      member?.username,
      member?.id,
    ]
      .filter((value) => typeof value === 'string' && value.trim() !== '')
      .join(' ')
      .toLowerCase();

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div className="card" style={{ padding: 12 }}>
        <h4 style={{ marginTop: 0 }}>Add from preset</h4>
        {presetRoles.length === 0 ? (
          <p className="text-muted" style={{ marginBottom: 0 }}>
            No role presets yet. Add one below.
          </p>
        ) : (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              className="input"
              value={presetId}
              onChange={(e) => setPresetId(e.target.value)}
              style={{ minWidth: 0, flex: '1 1 220px' }}
            >
              <option value="">Select role preset</option>
              {presetRoles.map((preset) => (
                <option key={preset.id} value={preset.id}>
                  {preset.name} ({Math.max(1, Number(preset.defaultSlots) || 1)} slots, {preset.requiresApproval === false ? 'first come' : 'approval'})
                </option>
              ))}
            </select>
            <button type="button" className="button button--xs" onClick={handleAddPreset}>
              Add preset role
            </button>
          </div>
        )}
      </div>

      <div className="card" style={{ padding: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
          <h4 style={{ margin: 0 }}>Quick add role</h4>
          {!quickAddOpen && (
            <button
              type="button"
              className="button button--xs"
              onClick={() => setQuickAddOpen(true)}
            >
              Add role
            </button>
          )}
        </div>

        {quickAddOpen && (
          <>
            <div className="form-grid" style={{ marginTop: 8 }}>
              <label className="form-field">
                <span className="field-label">Role name</span>
                <input
                  className="input"
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  placeholder="e.g. Marshal"
                />
              </label>

              <label className="form-field">
                <span className="field-label">Slots</span>
                <input
                  className="input"
                  type="number"
                  min="1"
                  value={newRoleSlots}
                  onChange={(e) => setNewRoleSlots(e.target.value)}
                />
              </label>
            </div>

            <div className="checkbox-row" style={{ marginTop: 8 }}>
              <input
                type="checkbox"
                id="quickRoleRequiresApproval"
                checked={newRoleRequiresApproval}
                onChange={(e) => setNewRoleRequiresApproval(e.target.checked)}
              />
              <label htmlFor="quickRoleRequiresApproval">Require admin approval for volunteer requests</label>
            </div>

            <div style={{ marginTop: 8 }}>
              <div className="text-muted" style={{ marginBottom: 6 }}>Allowed tags (optional)</div>
              {tagOptions.length === 0 ? (
                <p className="text-muted" style={{ marginBottom: 0 }}>No tags available.</p>
              ) : (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {tagOptions.map((tagName) => (
                    <label key={`quick:${tagName}`} style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={newRoleAllowedTags.includes(tagName)}
                        onChange={() => toggleQuickTag(tagName)}
                      />
                      {tagName}
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="checkbox-row" style={{ marginTop: 8 }}>
              <input
                type="checkbox"
                id="saveRolePreset"
                checked={saveAsPreset}
                onChange={(e) => setSaveAsPreset(e.target.checked)}
              />
              <label htmlFor="saveRolePreset">Save this role as a reusable team preset</label>
            </div>

            <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" className="button button--xs" onClick={handleQuickAdd}>
                Add role
              </button>
              <button
                type="button"
                className="button button-secondary button--xs"
                onClick={() => setQuickAddOpen(false)}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>

      <div className="card" style={{ padding: 12 }}>
        <h4 style={{ marginTop: 0 }}>Roles on this event</h4>
        {coverageHint && (
          <p className="text-muted" style={{ marginTop: -4, marginBottom: 8 }}>
            {coverageHint}
          </p>
        )}
        {roles.length === 0 ? (
          <p className="text-muted" style={{ marginBottom: 0 }}>
            No roles configured on this event.
          </p>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {roles.map((role, index) => {
              const slotCount = Array.isArray(role.slots) ? role.slots.length : 0;
              return (
                <div key={role.id || `role-${index}`} className="card" style={{ padding: 10 }}>
                  <div className="form-grid">
                    <label className="form-field">
                      <span className="field-label">Role name</span>
                      <input
                        className="input"
                        value={role.name || ''}
                        onChange={(e) => onUpdateRole?.(index, { name: e.target.value })}
                      />
                    </label>

                    <label className="form-field">
                      <span className="field-label">Slots</span>
                      <input
                        className="input"
                        type="number"
                        min="1"
                        value={Math.max(1, slotCount || 1)}
                        onChange={(e) => onResizeSlots?.(index, e.target.value)}
                      />
                    </label>
                  </div>

                  <div className="checkbox-row" style={{ marginTop: 8 }}>
                    <input
                      type="checkbox"
                      id={`role-approval-${role.id || index}`}
                      checked={role.requiresApproval !== false}
                      onChange={(e) =>
                        onUpdateRole?.(index, { requiresApproval: e.target.checked })
                      }
                    />
                    <label htmlFor={`role-approval-${role.id || index}`}>
                      Require admin approval
                    </label>
                  </div>

                  <div style={{ marginTop: 8 }}>
                    <div className="text-muted" style={{ marginBottom: 6 }}>Allowed tags (optional)</div>
                    {tagOptions.length === 0 ? (
                      <p className="text-muted" style={{ marginBottom: 0 }}>No tags available.</p>
                    ) : (
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {tagOptions.map((tagName) => (
                          <label key={`${role.id || index}:${tagName}`} style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                            <input
                              type="checkbox"
                              checked={Array.isArray(role.allowedTags) && role.allowedTags.includes(tagName)}
                              onChange={() => onToggleAllowedTag?.(index, tagName)}
                            />
                            {tagName}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  <div style={{ marginTop: 8 }}>
                    <div className="text-muted" style={{ marginBottom: 4 }}>Current slot assignments</div>
                    {(role.slots || []).length === 0 ? (
                      <p className="text-muted" style={{ marginBottom: 0 }}>No slots configured.</p>
                    ) : (
                      <div style={{ display: 'grid', gap: 6 }}>
                        {(role.slots || []).map((slot, slotIndex) => {
                          const slotKey = slot.id || `${index}:${slotIndex}`;
                          const selectedMemberId = assignBySlot[slotKey] || '';
                          const slotSearch = searchBySlot[slotKey] || '';
                          const normalizedSlotSearch = normalizeSearch(slotSearch);
                          const filteredMembers = normalizedSlotSearch
                            ? memberOptions.filter((member) =>
                                getMemberSearchText(member).includes(normalizedSlotSearch)
                              )
                            : memberOptions;
                          const selectedMember = memberOptions.find(
                            (member) => member.id === selectedMemberId
                          );
                          const isSelectedOutsideFilter =
                            !!selectedMemberId &&
                            !filteredMembers.some((member) => member.id === selectedMemberId);
                          const isAssigned = !!slot.assigneeUserId;
                          return (
                            <div key={slotKey} style={{ display: 'grid', gap: 6 }}>
                              <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                                <strong>Slot {slotIndex + 1}:</strong>{' '}
                                {slot.assigneeDisplayName || (slot.assigneeUserId ? 'Member' : 'Vacant')}
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  flexWrap: 'wrap',
                                }}
                              >
                                <input
                                  className="input"
                                  type="search"
                                  value={slotSearch}
                                  placeholder="Search first name, surname, username"
                                  onChange={(e) =>
                                    setSearchBySlot((prev) => ({
                                      ...prev,
                                      [slotKey]: e.target.value,
                                    }))
                                  }
                                  style={{ minWidth: 0, flex: '1 1 220px', maxWidth: '100%' }}
                                />
                                <select
                                  className="input"
                                  style={{ minWidth: 0, flex: '1 1 220px', maxWidth: '100%' }}
                                  value={selectedMemberId}
                                  onChange={(e) =>
                                    setAssignBySlot((prev) => ({
                                      ...prev,
                                      [slotKey]: e.target.value,
                                    }))
                                  }
                                >
                                  <option value="">
                                    {filteredMembers.length === 0 ? 'No matches found' : 'Select member'}
                                  </option>
                                  {isSelectedOutsideFilter && selectedMember && (
                                    <option value={selectedMember.id}>
                                      {selectedMember.name}
                                    </option>
                                  )}
                                  {filteredMembers.map((member) => (
                                    <option key={`${slotKey}:${member.id}`} value={member.id}>
                                      {member.name}
                                    </option>
                                  ))}
                                </select>
                                <button
                                  type="button"
                                  className="button button--xs"
                                  disabled={!selectedMemberId}
                                  onClick={() =>
                                    onAssignSlot?.({
                                      roleIndex: index,
                                      slotId: slot.id,
                                      memberId: selectedMemberId,
                                    })
                                  }
                                >
                                  {coverageScope === 'series' ? 'Assign to series' : 'Assign'}
                                </button>
                                {isAssigned && (
                                  <button
                                    type="button"
                                    className="button button--xs"
                                    onClick={() =>
                                      onClearSlot?.({
                                        roleIndex: index,
                                        slotId: slot.id,
                                      })
                                    }
                                  >
                                    {coverageScope === 'series' ? 'Clear series assignment' : 'Clear'}
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="form-actions" style={{ marginTop: 8 }}>
                    <button type="button" className="button button--xs" onClick={() => onRemoveRole?.(index)}>
                      Remove role
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
